import {
  tellerConfigByChain,
  tellerPoolsByChain,
  TellerPoolConfig,
} from '@1delta/data-sdk'
import { TellerV2Abi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { Call } from '../../../utils/multicall'
import { discoverTellerBidsViaLens, usesTellerLens } from './lens'

/**
 * User-data call build for Teller. Borrower loans ("bids") are keyed by a
 * uint256 bidId and a user can hold MANY, so this is a DISCOVERY-first build
 * (async, same pattern as the Liquity family):
 *
 *  1. `TellerV2.getBorrowerActiveLoanIds(account)` → the active bidIds;
 *  2. `TellerV2.getLoanLender(bidId)` per id → attribute each bid to a curated
 *     pool (the pool is the lender-of-record after `acceptFundsForAcceptBid`);
 *  3. data phase per kept bid: `bids`, `calculateAmountOwed(now)`,
 *     `CollateralManager.getCollateralAmount`, `isLoanDefaulted`.
 *
 * The discovered layout is stashed in a short-lived module cache so the parser
 * (a separate phase fed only the multicall results) can slice it — same trick
 * as Liquity / the Lista broker caches.
 */
export const TELLER_CALLS_PER_BID = 4
const CACHE_TTL_MS = 5 * 60_000

export interface TellerDiscoveredBid {
  /** decimal uint256 bidId */
  bidId: string
  /** the curated pool this bid was funded by */
  pool: TellerPoolConfig
}

export interface TellerDiscovery {
  bids: TellerDiscoveredBid[]
  at: number
}

const discoveryCache = new Map<string, TellerDiscovery>()
const cacheKey = (chainId: string, account: string) =>
  `${chainId}:${account.toLowerCase()}`

export const getCachedTellerBids = (
  chainId: string,
  account: string,
): TellerDiscovery | undefined => {
  const hit = discoveryCache.get(cacheKey(chainId, account))
  if (!hit || Date.now() - hit.at > CACHE_TTL_MS) return undefined
  return hit
}

const nowSec = () => Math.floor(Date.now() / 1000)

const toBigints = (arr: any): string[] => {
  if (!Array.isArray(arr)) return []
  const out: string[] = []
  for (const x of arr) {
    try {
      out.push(BigInt(x).toString())
    } catch {}
  }
  return out
}

export const buildTellerUserCall = async (
  chainId: string,
  _lender: string,
  account: string,
): Promise<Call[]> => {
  const proto = tellerConfigByChain(chainId)
  const pools = tellerPoolsByChain(chainId)
  if (!proto?.tellerV2 || !proto?.collateralManager || pools.length === 0) {
    return []
  }

  const poolByAddrEarly = new Map<string, TellerPoolConfig>()
  for (const p of pools) poolByAddrEarly.set(p.pool.toLowerCase(), p)

  // ONE call replaces both discovery phases where the lens is deployed. A failure
  // falls through to the two-phase walk below rather than reporting no loans.
  if (usesTellerLens(chainId)) {
    const viaLens = await discoverTellerBidsViaLens(
      chainId,
      proto.tellerV2,
      account,
    )
    if (viaLens) {
      const keptLens: TellerDiscoveredBid[] = []
      for (const b of viaLens) {
        const pool = poolByAddrEarly.get(b.lender.toLowerCase())
        // Same curation as the walk: a bid funded by a lender outside the
        // allowlist is not ours to report.
        if (pool) keptLens.push({ bidId: b.bidId, pool })
      }
      discoveryCache.set(cacheKey(chainId, account), {
        bids: keptLens,
        at: Date.now(),
      })
      return buildTellerDataPhase(proto, keptLens, account)
    }
  }

  // Phase 1 — discover the borrower's active bidIds.
  let ids: string[] = []
  try {
    const res = (await multicallRetryUniversal({
      chain: chainId,
      calls: [
        {
          address: proto.tellerV2,
          name: 'getBorrowerActiveLoanIds',
          params: [account],
        },
      ],
      abi: TellerV2Abi as any,
      allowFailure: true,
    })) as any[]
    ids = toBigints(res?.[0])
  } catch {
    return []
  }
  if (ids.length === 0) {
    discoveryCache.set(cacheKey(chainId, account), { bids: [], at: Date.now() })
    return []
  }

  // Phase 2 — attribute each bid to a curated pool via its lender-of-record.
  const poolByAddr = new Map<string, TellerPoolConfig>()
  for (const p of pools) poolByAddr.set(p.pool.toLowerCase(), p)

  let lenders: any[] = []
  try {
    lenders = (await multicallRetryUniversal({
      chain: chainId,
      calls: ids.map((id) => ({
        address: proto.tellerV2,
        name: 'getLoanLender',
        params: [id],
      })),
      abi: TellerV2Abi as any,
      allowFailure: true,
    })) as any[]
  } catch {
    return []
  }

  const kept: TellerDiscoveredBid[] = []
  ids.forEach((id, i) => {
    const r = lenders[i]
    const lenderAddr = typeof r === 'string' ? r.toLowerCase() : ''
    const pool = poolByAddr.get(lenderAddr)
    if (pool) kept.push({ bidId: id, pool })
  })

  discoveryCache.set(cacheKey(chainId, account), { bids: kept, at: Date.now() })
  return buildTellerDataPhase(proto, kept, account)
}

/**
 * Phase 3, identical for both discovery paths: per kept bid, the bid struct, the
 * amount owed now, the posted collateral and the default flag.
 *
 * Shared deliberately — the lens answers WHICH bids, not what they are worth, so
 * the numbers a converter reads come from exactly the same calls either way and
 * the two paths cannot drift in interpretation.
 */
function buildTellerDataPhase(
  proto: { tellerV2: string; collateralManager: string },
  kept: TellerDiscoveredBid[],
  _account: string,
): Call[] {
  if (kept.length === 0) return []
  const ts = nowSec()
  const calls: Call[] = []
  for (const b of kept) {
    calls.push({ address: proto.tellerV2, name: 'bids', params: [b.bidId] })
    calls.push({
      address: proto.tellerV2,
      name: 'calculateAmountOwed',
      params: [b.bidId, ts],
    })
    calls.push({
      address: proto.collateralManager,
      name: 'getCollateralAmount',
      params: [b.bidId, b.pool.collateral],
    })
    calls.push({
      address: proto.tellerV2,
      name: 'isLoanDefaulted',
      params: [b.bidId],
    })
  }
  return calls
}
